import {
  Stack,
  Text,
  Icon,
  Badge,
  Label,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';
import { formatThroughput, processingStatusLabel } from '../utils';

interface StatsPanelProps {
  data: ServicesApiEnvHealthResponse;
  type?: 'ingress' | 'egress';
  onStatusClick: () => void;
}

export function StatsPanel({ data, type, onStatusClick }: StatsPanelProps) {
  const { processing, stats } = data;
  const isIngress = type === 'ingress';
  const isEgress = type === 'egress';
  const isDirectional = type != null;

  const drainSeconds = isIngress
    ? processing.drain_seconds ?? 0
    : isEgress
    ? data.egress.drain_seconds ?? 0
    : Math.max(processing.drain_seconds ?? 0, data.egress.drain_seconds ?? 0);
  const pStatus = processingStatusLabel(drainSeconds > 0 ? drainSeconds : null);

  const throughputTooltip = isEgress
    ? 'How fast outgoing messages (email, push, SMS, webhooks) are being sent right now.'
    : 'How fast incoming data (events, profile updates, imports) is being processed right now. Low throughput can simply mean there is less activity to process.';
  const throughputValue = isEgress ? stats.outgoing_throughput_per_second : stats.throughput_per_second;
  const processedToday = isEgress
    ? stats.outgoing_processed_today
    : isIngress
    ? stats.processed_today
    : stats.processed_today + stats.egress_processed_today;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle text="product-h4" color="subtle">
          <InlineStack gap="050" alignBlock="center">
            <Icon name="dashboard" size="sm" color="bold" />
            Processing
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Stack gap="100">
          <InlineStack alignInline="space-between" alignBlock="center" py="050">
            <InlineStack gap="050" alignBlock="center">
              <Text size="s" color="subtle">
                Processing rate
              </Text>
              <Tooltip>
                <Icon name="help" size="xs" color="subtle" />
                <TooltipContent>{throughputTooltip}</TooltipContent>
              </Tooltip>
            </InlineStack>
            <Badge>{formatThroughput(throughputValue)}</Badge>
          </InlineStack>
          {!isDirectional ? (
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s" color="subtle">
                  Outgoing rate
                </Text>
                <Tooltip>
                  <Icon name="help" size="xs" color="subtle" />
                  <TooltipContent>
                    How fast outgoing messages (email, push, SMS, webhooks) are being sent right now.
                  </TooltipContent>
                </Tooltip>
              </InlineStack>
              <Badge>{formatThroughput(stats.outgoing_throughput_per_second)}</Badge>
            </InlineStack>
          ) : null}
          <InlineStack alignInline="space-between" alignBlock="center" py="050">
            <Text size="s" color="subtle">
              Processed today
            </Text>
            <Badge>{processedToday.toLocaleString()}</Badge>
          </InlineStack>
          <InlineStack alignInline="space-between" alignBlock="center" py="050">
            <Text size="s" color="subtle">
              Status
            </Text>
            <Link variant="secondary" isUnderlined={false} onClick={onStatusClick}>
              <InlineStack gap="050" alignBlock="center">
                <Label color={pStatus.color} showStatusIndicator>
                  {pStatus.label}
                </Label>
                <Icon name="chevron-right" size="xs" color="subtle" />
              </InlineStack>
            </Link>
          </InlineStack>
        </Stack>
      </PanelSection>
    </Panel>
  );
}
